import { useEffect, useState, useRef } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { ApiResourceTable } from "@project/admin/components/api-resource-table";
import { SearchableSelect } from "@project/admin/components/searchable-select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@project/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@project/components/ui/dialog";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import type { Field } from "@project/admin/components/resource-table";
import type { LookupOption } from "@project/admin/components/api-resource-table";
import { projectToast } from "@project/lib/toast";
import {
  animalService,
  animalHealthLogService,
  animalMilkYieldService,
  dairyProductUsageService,
} from "@project/services/admin";
import type { Animal, FamilyTreeNode, DairyProductUsage, MilkUsageRecord } from "@project/types/vrindavan-gaushala";
import {
  Sprout,
  Activity,
  GlassWater,
  Network,
  Pencil,
  Milk,
  Trash2,
} from "lucide-react";
import { formatDate } from "@project/lib/date-format";

// Fields configuration
const animalFields: Field[] = [
  {
    name: "animal_name",
    label: "Name",
    required: true,
    showInTable: true,
  },
  {
    name: "species",
    label: "Species",
    type: "select",
    options: [
      { value: "Cow", label: "Cow" },
      { value: "Bull", label: "Bull" },
      { value: "Calf", label: "Calf" },
      { value: "Heifer", label: "Heifer" },
    ],
    required: true,
    showInTable: true,
  },
  {
    name: "breed",
    label: "Breed",
    required: true,
    showInTable: true,
  },
  {
    name: "age_years",
    label: "Age (Years)",
    type: "number",
    required: true,
    showInTable: true,
  },
  {
    name: "age_months",
    label: "Age (Months)",
    type: "number",
    required: true,
    showInTable: true,
  },
  {
    name: "health_status",
    label: "Health Status",
    type: "select",
    options: [
      { value: "Healthy", label: "Healthy" },
      { value: "Sick", label: "Sick" },
      { value: "Under Treatment", label: "Under Treatment" },
      { value: "Recovered", label: "Recovered" },
    ],
    required: true,
    showInTable: true,
  },
  {
    name: "daily_yield",
    label: "Daily Yield (L)",
    type: "number",
    required: true,
    showInTable: true,
  },
  {
    name: "status",
    label: "Status",
    type: "select",
    options: [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
      { value: "sold", label: "Sold" },
      { value: "deceased", label: "Deceased" },
    ],
    required: true,
    showInTable: true,
  },
  {
    name: "entry_type",
    label: "Entry Type",
    type: "select",
    options: [
      { value: "birth", label: "Birth" },
      { value: "purchase", label: "Purchase" },
      { value: "donation", label: "Donation" },
    ],
    required: true,
    showInTable: false,
  },
  {
    name: "entry_date",
    label: "Entry Date",
    type: "date",
    required: true,
    showInTable: false,
  },
  {
    name: "mother",
    label: "Mother",
    type: "select",
    required: false,
    showInTable: false,
    lookup: { table: "animal" },
  },
  {
    name: "father",
    label: "Father",
    type: "select",
    required: false,
    showInTable: false,
    lookup: { table: "animal" },
  },
  {
    name: "death_date",
    label: "Death Date",
    type: "date",
    required: false,
    showInTable: false,
  },
  {
    name: "death_reason",
    label: "Death Reason",
    type: "text",
    required: false,
    showInTable: false,
  },
];

const healthFields: Field[] = [
  {
    name: "animal",
    label: "Animal",
    type: "select",
    required: true,
    showInTable: true,
    lookup: { table: "animal" },
  },
  {
    name: "log_type",
    label: "Log Type",
    type: "select",
    options: [
      { value: "Vaccination", label: "Vaccination" },
      { value: "Checkup", label: "Checkup" },
      { value: "Treatment", label: "Treatment" },
      { value: "Deworming", label: "Deworming" },
      { value: "Other", label: "Other" },
    ],
    required: true,
    showInTable: true,
  },
  {
    name: "date",
    label: "Date",
    type: "date",
    required: true,
    showInTable: true,
  },
  {
    name: "veterinarian",
    label: "Veterinarian",
    required: true,
    showInTable: true,
  },
  {
    name: "next_booster_date",
    label: "Next Booster Date",
    type: "date",
    required: false,
    showInTable: true,
  },
  {
    name: "post_check_health_status",
    label: "Post-Check Health Status",
    type: "select",
    options: [
      { value: "Healthy", label: "Healthy" },
      { value: "Sick", label: "Sick" },
      { value: "Under Treatment", label: "Under Treatment" },
      { value: "Recovered", label: "Recovered" },
    ],
    required: true,
    showInTable: false,
  },
  {
    name: "veterinary_notes",
    label: "Veterinary Notes",
    type: "textarea",
    required: false,
    showInTable: false,
  },
];

const milkFields: Field[] = [
  {
    name: "animal",
    label: "Animal",
    type: "select",
    required: true,
    showInTable: true,
    lookup: { table: "animal" },
  },
  {
    name: "date",
    label: "Date",
    type: "date",
    required: true,
    showInTable: true,
  },
  {
    name: "total_milk",
    label: "Total Milk (L)",
    type: "number",
    required: true,
    showInTable: true,
  },
  {
    name: "remaining_milk",
    label: "Remaining Milk (L)",
    type: "number",
    required: true,
    showInTable: true,
  },
];

const animalDefaults = {
  species: "Cow",
  breed: "Gir",
  health_status: "Healthy",
  status: "active",
  entry_type: "birth",
  entry_date: new Date().toISOString().split("T")[0],
};

const healthDefaults = {
  log_type: "Vaccination",
  date: new Date().toISOString().split("T")[0],
  post_check_health_status: "Healthy",
};

const milkDefaults = {
  date: new Date().toISOString().split("T")[0],
  session: "Morning",
  yield_amount: "",
};

const dairyFields: Field[] = [
  {
    name: "product",
    label: "Product",
    type: "select",
    options: [
      { value: "Ghee", label: "Ghee" },
      { value: "Paneer", label: "Paneer" },
      { value: "Curd", label: "Curd(Dahi)" },
      { value: "Buttermilk", label: "Buttermilk" },
    ],
    required: true,
    showInTable: true,
  },
  {
    name: "date",
    label: "Date",
    type: "date",
    required: true,
    showInTable: true,
  },
  {
    name: "milk_usages_summary",
    label: "Milk Usages",
    type: "text",
    showInTable: true,
    readOnly: true,
  },
];

const dairyDefaults = {
  product: "Ghee",
  date: new Date().toISOString().split("T")[0],
  milk_usages: [],
};

export default function VrindavanGaushalaPage() {
  const [animalOptions, setAnimalOptions] = useState<LookupOption[]>([]);
  const [milkYieldOptions, setMilkYieldOptions] = useState<LookupOption[]>([]);
  const [rawMilkYieldLogs, setRawMilkYieldLogs] = useState<any[]>([]);
  const originalUsagesRef = useRef<Record<number, number>>({});
  const [activeTab, setActiveTab] = useState("animals");

  const [deathAnimal, setDeathAnimal] = useState<Animal | null>(null);
  const [deathDate, setDeathDate] = useState("");
  const [deathReason, setDeathReason] = useState("");
  const [deathDialogOpen, setDeathDialogOpen] = useState(false);
  const [savingDeath, setSavingDeath] = useState(false);
  const refreshRef = useRef<() => void>(() => { });

  const handleSaveDeathDetails = async () => {
    if (!deathAnimal) return;
    if (!deathDate) {
      projectToast.error("Please select a death date");
      return;
    }
    if (!deathReason.trim()) {
      projectToast.error("Please enter a reason for death");
      return;
    }
    setSavingDeath(true);
    try {
      await animalService.update(deathAnimal.id, {
        status: "deceased",
        death_date: deathDate,
        death_reason: deathReason,
      });
      projectToast.success("Animal status updated to deceased");
      setDeathDialogOpen(false);
      if (refreshRef.current) {
        refreshRef.current();
      }
    } catch (err) {
      console.error(err);
      projectToast.error("Failed to update death details");
    } finally {
      setSavingDeath(false);
    }
  };

  const fetchAnimalOptions = async (): Promise<LookupOption[]> => {
    try {
      const res = await animalService.listPage({ page: 1, page_size: 1000 });
      const options = (res.data || []).map((a) => ({
        value: String(a.id),
        label: `${a.animal_name} (${a.breed} - ID: ${a.id})`,
      }));
      setAnimalOptions(options);
      return options;
    } catch (err) {
      console.error(err);
      projectToast.error("Failed to load animals list");
      return [];
    }
  };

  const fetchMilkYieldOptions = async (): Promise<LookupOption[]> => {
    try {
      const res = await animalMilkYieldService.listPage({ page: 1, page_size: 1000 });
      const logs = res.data || [];
      setRawMilkYieldLogs(logs);

      const options = logs.map((y: any) => {
        const sessionDetails = Object.entries(y.session_yields || {})
          .map(([session, amt]) => `${session}: ${amt}L`)
          .join(", ");
        return {
          value: String(y.id),
          label: `Yield #${y.id} - ${y.animal_name || `Animal #${y.animal}`} (${formatDate(y.date)}) - Total: ${y.total_milk}L ${sessionDetails ? `(${sessionDetails})` : ""}`,
        };
      });
      setMilkYieldOptions(options);
      return options;
    } catch (err) {
      console.error(err);
      projectToast.error("Failed to load milk yield options");
      return [];
    }
  };

  useEffect(() => {
    void fetchAnimalOptions();
  }, []);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    void fetchAnimalOptions();
    if (tab === "dairy") {
      void fetchMilkYieldOptions();
    }
  };

  return (
    <AdminShell
      title="Vrindavan Gir Gaushala"
      subtitle="Manage animal directory, health & vaccination logs, milk yield logs, and view family tree lineage"
    >
      <Tabs value={activeTab} onValueChange={handleTabChange} variant="line" className="space-y-4">
        <div className="-mx-1 overflow-x-auto px-1 pb-1">
          <TabsList>
            <TabsTrigger value="animals">
              <Sprout className="h-4 w-4 shrink-0" />
              Animal Directory
            </TabsTrigger>
            <TabsTrigger value="health">
              <Activity className="h-4 w-4 shrink-0" />
              Health &amp; Vaccine Logs
            </TabsTrigger>
            <TabsTrigger value="milk">
              <GlassWater className="h-4 w-4 shrink-0" />
              Milk Yield Logs
            </TabsTrigger>
            <TabsTrigger value="dairy">
              <Milk className="h-4 w-4 shrink-0" />
              Dairy Product Usages
            </TabsTrigger>
            <TabsTrigger value="tree">
              <Network className="h-4 w-4 shrink-0" />
              Family Tree
            </TabsTrigger>
          </TabsList>
        </div>




        <TabsContent value="animals" className="mt-0">
          <ApiResourceTable
            title="Animal"
            fields={animalFields}
            labelField="animal_name"
            searchFields={["animal_name", "breed"]}
            list={animalService.listPage}
            create={animalService.create}
            update={animalService.update}
            remove={animalService.delete}
            defaultValues={animalDefaults}
            enableDateFilter={true}
            lookupOptions={{
              animal: animalOptions,
              mother: animalOptions,
              father: animalOptions,
            }}
            lookupLoaders={{
              animal: fetchAnimalOptions,
              mother: fetchAnimalOptions,
              father: fetchAnimalOptions,
            }}
            csvFileName="animals-directory"
            extraColumns={[
              {
                id: "death_action",
                label: "Death Details",
                render: (row, { refresh }) => {
                  if (row.status === "deceased") {
                    return (
                      <div className="flex items-center justify-center">
                        <button
                          type="button"
                          onClick={() => {
                            setDeathAnimal(row);
                            setDeathDate(row.death_date || "");
                            setDeathReason(row.death_reason || "");
                            refreshRef.current = refresh;
                            setDeathDialogOpen(true);
                          }}
                          className="text-xs font-bold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors shadow-none"
                        >
                          <span>Deceased ({formatDate(row.death_date) || "No Date"})</span>
                          <Pencil className="h-3.5 w-3.5 text-red-500" />
                        </button>
                      </div>
                    );
                  }
                  return (
                    <div className="flex items-center justify-center">
                      <Button
                        type="button"
                        onClick={() => {
                          setDeathAnimal(row);
                          setDeathDate(row.death_date || new Date().toISOString().split("T")[0]);
                          setDeathReason(row.death_reason || "");
                          refreshRef.current = refresh;
                          setDeathDialogOpen(true);
                        }}
                        className="h-7 text-xs font-bold px-3.5 py-1 rounded-full bg-red-600 hover:bg-red-700 text-white transition-all shadow-sm hover:shadow-md border-none"
                      >
                        Mark Deceased
                      </Button>
                    </div>
                  );
                },
              },
            ]}
          />
        </TabsContent>

        <TabsContent value="health" className="mt-0">
          <ApiResourceTable
            title="Health Log"
            fields={healthFields}
            searchFields={["veterinarian", "log_type"]}
            list={animalHealthLogService.listPage}
            create={animalHealthLogService.create}
            update={animalHealthLogService.update}
            remove={animalHealthLogService.delete}
            defaultValues={healthDefaults}
            enableDateFilter={true}
            lookupOptions={{
              animal: animalOptions,
            }}
            lookupLoaders={{
              animal: fetchAnimalOptions,
            }}
            csvFileName="animal-health-vaccine-logs"
          />
        </TabsContent>

        <TabsContent value="milk" className="mt-0">
          <ApiResourceTable
            title="Milk Yield"
            fields={milkFields}
            searchFields={["animal_name"]}
            list={async (query) => {
              const res = await animalMilkYieldService.listPage(query);
              const data = (res.data || []).map((item: any) => ({
                ...item,
                animal_name: item.animal_name || `Animal #${item.animal}`,
              }));
              return { ...res, data };
            }}
            renderExpandableRow={(r: any) => {
              const sessions = Object.entries(r.session_yields || {});
              return (
                <div className="border border-border/60 rounded-xl overflow-hidden bg-card max-w-full mx-auto shadow-xs">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-muted/40 border-b border-border/55">
                        <th className="p-2.5 font-bold text-muted-foreground w-1/2">Session</th>
                        <th className="p-2.5 font-bold text-muted-foreground w-1/2">Yield Amount (L)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/40">
                      {sessions.map(([session, yieldVal]) => (
                        <tr key={session} className="hover:bg-muted/10 transition-colors">
                          <td className="p-2.5 font-medium text-foreground capitalize">{session} Session</td>
                          <td className="p-2.5 text-foreground">{yieldVal} L</td>
                        </tr>
                      ))}
                      <tr className="bg-muted/10 font-bold border-t border-border">
                        <td className="p-2.5 text-foreground">Total Daily Yield</td>
                        <td className="p-2.5 text-foreground">{r.total_milk} L</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            }}
            create={animalMilkYieldService.create}
            update={animalMilkYieldService.update}
            remove={animalMilkYieldService.delete}
            defaultValues={milkDefaults}
            enableDateFilter={true}
            lookupOptions={{
              animal: animalOptions,
            }}
            lookupLoaders={{
              animal: fetchAnimalOptions,
            }}
            csvFileName="animal-milk-yield-logs"
            mapRecordToForm={(record: any) => {
              return {
                ...record,
                session: "Morning",
                yield_amount: record.session_yields?.Morning || "",
                original_session_yields: record.session_yields || {},
              };
            }}
            buildPayload={(editing: any) => {
              return {
                animal: Number(editing.animal),
                date: editing.date,
                session: editing.session || "Morning",
                yield_amount: String(editing.yield_amount || "0.00"),
              };
            }}
            renderForm={({ editing, setEditing }) => {
              return (
                <div className="space-y-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="milk-animal" data-required="true">Animal</Label>
                      <select
                        id="milk-animal"
                        value={editing?.animal || ""}
                        onChange={(e) => setEditing((prev: any) => ({ ...prev, animal: e.target.value }))}
                        className="bg-muted/40 h-11 w-full rounded-xl border-transparent px-3 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
                        required
                      >
                        <option value="">Select Animal...</option>
                        {animalOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="milk-date" data-required="true">Date</Label>
                      <Input
                        id="milk-date"
                        type="date"
                        value={editing?.date || ""}
                        onChange={(e) => setEditing((prev: any) => ({ ...prev, date: e.target.value }))}
                        required
                        className="bg-muted/40 rounded-xl border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2 pt-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="milk-session" data-required="true">Session</Label>
                      <select
                        id="milk-session"
                        value={editing?.session || "Morning"}
                        onChange={(e) => {
                          const newSession = e.target.value;
                          const amount = editing?.original_session_yields?.[newSession] || "";
                          setEditing((prev: any) => ({
                            ...prev,
                            session: newSession,
                            yield_amount: amount,
                          }));
                        }}
                        className="bg-muted/40 h-11 w-full rounded-xl border-transparent px-3 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
                        required
                      >
                        <option value="Morning">Morning</option>
                        <option value="Evening">Evening</option>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="yield-amount" data-required="true">Yield Amount (L)</Label>
                      <Input
                        id="yield-amount"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="e.g. 8.50"
                        value={editing?.yield_amount || ""}
                        onChange={(e) => setEditing((prev: any) => ({ ...prev, yield_amount: e.target.value }))}
                        required
                        className="bg-muted/40 rounded-xl border-transparent"
                      />
                    </div>
                  </div>
                </div>
              );
            }}
          />
        </TabsContent>

        <TabsContent value="dairy" className="mt-0">
          <ApiResourceTable
            title="Dairy Product Usage"
            fields={dairyFields}
            searchFields={["product"]}
            list={async (query) => {
              const res = await dairyProductUsageService.listPage(query);
              const data = (res.data || []).map((item) => {
                const usages = item.usage_details || item.milk_usages || [];
                const summary = usages
                  .map((mu: any) => {
                    const animalName = mu.animal || mu.milk_yield_details?.animal_name || `Yield #${mu.milk_yield}`;
                    return `${animalName} (${mu.milk_used}L)`;
                  })
                  .join(", ") || "—";
                return {
                  ...item,
                  milk_usages_summary: summary,
                };
              });
              return { ...res, data };
            }}
            create={dairyProductUsageService.create}
            update={dairyProductUsageService.update}
            remove={dairyProductUsageService.delete}
            defaultValues={dairyDefaults}
            enableDateFilter={true}
            csvFileName="dairy-product-usages"
            mapRecordToForm={(record: any) => {
              // Store original used quantities for edit limit checks
              const originalMap: Record<number, number> = {};
              const usages = record.usage_details || record.milk_usages || [];
              usages.forEach((mu: any) => {
                originalMap[Number(mu.milk_yield)] = Number(mu.milk_used || 0);
              });
              originalUsagesRef.current = originalMap;
              return {
                ...record,
                milk_usages: usages.map((mu: any) => ({
                  milk_yield: mu.milk_yield,
                  milk_used: mu.milk_used,
                  milk_yield_details: mu.milk_yield_details || {
                    animal_name: mu.animal || `Yield #${mu.milk_yield}`,
                    date: mu.milk_yield_date || record.date,
                    remaining_milk: mu.remaining_milk || "0.00",
                  }
                }))
              };
            }}
            buildPayload={(editing) => {
              if (editing.product === "Other" || !editing.product) {
                projectToast.error("Please specify a custom product name");
                return null;
              }

              const usages = (editing as any).milk_usages || [];
              for (const usage of usages) {
                const details = rawMilkYieldLogs.find((l) => l.id === Number(usage.milk_yield)) || usage.milk_yield_details;
                if (details) {
                  const originalUsed = originalUsagesRef.current[Number(usage.milk_yield)] || 0;
                  const maxAllowed = Number(details.remaining_milk || 0) + originalUsed;
                  if (Number(usage.milk_used || 0) > maxAllowed) {
                    projectToast.error(`Milk yield usage for ${details.animal_name || 'Animal'} exceeds maximum available (${maxAllowed.toFixed(2)} L)`);
                    return null;
                  }
                }
              }

              return {
                product: editing.product,
                date: editing.date,
                milk_usages: usages
                  .filter((mu: any) => mu.milk_yield && mu.milk_used)
                  .map((mu: any) => ({
                    milk_yield: Number(mu.milk_yield),
                    milk_used: String(mu.milk_used),
                  })),
              };
            }}
            renderForm={({ editing, setEditing }) => {
              const usages = (editing as any)?.milk_usages || [];

              useEffect(() => {
                void fetchMilkYieldOptions();
              }, []);

              const handleSelectYield = (e: React.ChangeEvent<HTMLSelectElement>) => {
                const selectedId = e.target.value;
                if (!selectedId) return;
                if (usages.some((u: any) => String(u.milk_yield) === selectedId)) {
                  projectToast.error("This Milk Yield Log has already been added.");
                  e.target.value = "";
                  return;
                }
                setEditing((prev: any) => ({
                  ...prev,
                  milk_usages: [
                    ...usages,
                    { milk_yield: Number(selectedId), milk_used: "" }
                  ]
                }));
                e.target.value = "";
              };

              const handleRemoveUsage = (index: number) => {
                setEditing((prev: any) => ({
                  ...prev,
                  milk_usages: usages.filter((_: any, idx: number) => idx !== index)
                }));
              };

              const handleUsageChange = (index: number, key: string, value: any) => {
                setEditing((prev: any) => {
                  const copy = [...usages];
                  copy[index] = { ...copy[index], [key]: value };
                  return { ...prev, milk_usages: copy };
                });
              };

              return (
                <div className="space-y-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="form-product" data-required="true">Product</Label>
                      {(() => {
                        const productOptions = ["Ghee", "Paneer", "Curd", "Buttermilk"];
                        const currentSelectValue = !editing?.product
                          ? ""
                          : productOptions.includes(editing.product)
                            ? editing.product
                            : "Other";

                        return (
                          <>
                            <select
                              id="form-product"
                              value={currentSelectValue}
                              onChange={(e) => {
                                const val = e.target.value;
                                setEditing((prev: any) => ({
                                  ...prev,
                                  product: val === "Other" ? "Other" : val,
                                }));
                              }}
                              className="bg-muted/40 h-11 w-full rounded-xl border-transparent px-3 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
                              required
                            >
                              <option value="">Select Product...</option>
                              <option value="Ghee">Ghee</option>
                              <option value="Paneer">Paneer</option>
                              <option value="Curd">Curd(Dahi)</option>
                              <option value="Buttermilk">Buttermilk</option>
                              <option value="Other">Other</option>
                            </select>

                            {currentSelectValue === "Other" && (
                              <div className="pt-1.5 space-y-1">
                                <Label htmlFor="form-product-other" data-required="true">Specify Product Name</Label>
                                <Input
                                  id="form-product-other"
                                  type="text"
                                  placeholder="Enter custom product name..."
                                  value={editing?.product === "Other" ? "" : (editing?.product || "")}
                                  onChange={(e) => setEditing((prev: any) => ({ ...prev, product: e.target.value || "Other" }))}
                                  required
                                  className="bg-muted/40 rounded-xl border-transparent h-10 text-sm"
                                />
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="form-date" data-required="true">Date</Label>
                      <Input
                        id="form-date"
                        type="date"
                        value={editing?.date || ""}
                        onChange={(e) => setEditing((prev: any) => ({ ...prev, date: e.target.value }))}
                        required
                        className="bg-muted/40 rounded-xl border-transparent"
                      />
                    </div>
                  </div>

                  <div className="space-y-3 pt-2">
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="add-milk-yield-select" className="text-sm font-bold">Add Milk Yield Source</Label>
                      <select
                        id="add-milk-yield-select"
                        value=""
                        onFocus={() => void fetchMilkYieldOptions()}
                        onChange={handleSelectYield}
                        className="bg-muted/40 h-11 w-full rounded-xl border-transparent px-3 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
                      >
                        <option value="">Choose a Milk Yield Log to link...</option>
                        {milkYieldOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="border border-border/60 rounded-2xl overflow-hidden bg-card/50">
                      <table className="w-full text-left text-xs border-collapse">
                        <thead>
                          <tr className="bg-muted/40 border-b border-border/55">
                            <th className="p-3 font-semibold text-muted-foreground w-3/12">Source Details</th>
                            <th className="p-3 font-semibold text-muted-foreground w-3/12">Sessions Breakdown</th>
                            <th className="p-3 font-semibold text-muted-foreground w-3/12">Available Milk</th>
                            <th className="p-3 font-semibold text-muted-foreground w-2/12">Milk Used (L)</th>
                            <th className="p-3 font-semibold text-muted-foreground text-right w-1/12">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usages.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="p-6 text-center text-muted-foreground">
                                No milk yields selected. Choose a yield source from the dropdown above to add it.
                              </td>
                            </tr>
                          ) : (
                            usages.map((usage: any, idx: number) => {
                              const details = rawMilkYieldLogs.find((l) => l.id === Number(usage.milk_yield)) || usage.milk_yield_details;
                              const animalName = details?.animal_name || `Yield #${usage.milk_yield}`;
                              const dateText = details?.date ? formatDate(details.date) : "";
                              const sessionsText = details?.session_yields
                                ? Object.entries(details.session_yields)
                                  .map(([s, amt]) => `${s}: ${amt}L`)
                                  .join(", ")
                                : "—";

                              const originalUsed = originalUsagesRef.current[Number(usage.milk_yield)] || 0;
                              const maxAllowed = details ? Number(details.remaining_milk || 0) + originalUsed : 0;
                              const isExceeded = Number(usage.milk_used || 0) > maxAllowed;

                              return (
                                <tr key={idx} className="border-b border-border/40 last:border-0 hover:bg-muted/20">
                                  <td className="p-3 font-medium text-foreground">
                                    <div className="font-semibold">{animalName}</div>
                                    <div className="text-muted-foreground text-[10px] mt-0.5">{dateText}</div>
                                  </td>
                                  <td className="p-3 text-muted-foreground">
                                    {sessionsText}
                                  </td>
                                  <td className="p-3 text-xs">
                                    {details ? (
                                      <div className="flex flex-col gap-1">
                                        <div className="text-muted-foreground">
                                          Total: <span className="font-extrabold text-foreground">{details.total_milk} L</span>
                                        </div>
                                        <div className="text-muted-foreground">
                                          Rem: <span className={`font-extrabold ${isExceeded ? "text-destructive" : "text-green-600 dark:text-green-400"}`}>
                                            {details.remaining_milk} L
                                          </span>
                                        </div>
                                      </div>
                                    ) : (
                                      "—"
                                    )}
                                  </td>
                                  <td className="p-3">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0.01"
                                      value={usage.milk_used || ""}
                                      onChange={(e) => handleUsageChange(idx, "milk_used", e.target.value)}
                                      placeholder="Used Litres"
                                      className={`bg-muted/40 h-10 rounded-xl border-transparent text-xs w-28 ${isExceeded ? "border-destructive ring-1 ring-destructive focus:border-destructive focus:ring-destructive text-destructive font-semibold" : ""
                                        }`}
                                      required
                                    />
                                    {isExceeded && (
                                      <div className="text-[10px] text-destructive font-bold mt-1.5 animate-pulse">
                                        Exceeds max {maxAllowed.toFixed(2)} L
                                      </div>
                                    )}
                                  </td>
                                  <td className="p-3 text-right">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleRemoveUsage(idx)}
                                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    >
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            }}
          />
        </TabsContent>

        <TabsContent value="tree" className="mt-0">
          <FamilyTreeView animalOptions={animalOptions} />
        </TabsContent>
      </Tabs>

      <Dialog open={deathDialogOpen} onOpenChange={setDeathDialogOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>
              {deathAnimal?.status === "deceased" ? "Edit Death Details" : "Mark Animal as Deceased"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="rounded-xl bg-muted/40 p-3 text-xs text-muted-foreground border border-border/30">
              <span className="font-extrabold uppercase text-[10px] block mb-1">Animal Info</span>
              <span className="font-bold text-foreground">{deathAnimal?.animal_name}</span> ({deathAnimal?.breed} • {deathAnimal?.species})
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="death_date" data-required="true">Death Date</Label>
              <Input
                id="death_date"
                type="date"
                value={deathDate}
                onChange={e => setDeathDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="death_reason" data-required="true">Reason for Death</Label>
              <Textarea
                id="death_reason"
                placeholder="Enter symptoms, illness, accident details, etc..."
                value={deathReason}
                onChange={e => setDeathReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              disabled={savingDeath}
              onClick={() => setDeathDialogOpen(false)}
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={savingDeath}
              onClick={handleSaveDeathDetails}
              className="rounded-xl"
            >
              {savingDeath ? "Saving..." : "Save Details"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}

// Helper to detect if a node is an ancestor of the selected animal
const checkIsAncestor = (n: FamilyTreeNode, targetId: number): boolean => {
  if (!n.children || n.children.length === 0) return false;
  return n.children.some(child => child.id === targetId || checkIsAncestor(child, targetId));
};

// Recursive Tree Node Component
interface TreeNodeProps {
  node: FamilyTreeNode;
  selectedId: number;
}

function TreeNodeComponent({ node, selectedId }: TreeNodeProps) {
  const isSelected = node.id === selectedId;
  const isParent = checkIsAncestor(node, selectedId);
  const hasChildren = node.children && node.children.length > 0;

  // Color styles based on node role
  let cardClass = "border-border/60 bg-card hover:border-[#0052b4]/40";
  let roleLabel = "";
  let roleColorClass = "text-muted-foreground";
  let labelBg = "bg-muted/10";

  if (isSelected) {
    cardClass = "border-[#0052b4] bg-[#0052b4]/5 ring-4 ring-[#0052b4]/10 scale-105";
    roleLabel = "Selected";
    roleColorClass = "text-[#0052b4]";
    labelBg = "bg-[#0052b4]/10";
  } else if (isParent) {
    cardClass = "border-emerald-500/40 bg-emerald-500/5 hover:border-emerald-500/70";
    roleLabel = "Parent / Ancestor";
    roleColorClass = "text-emerald-600";
    labelBg = "bg-emerald-500/10";
  } else {
    cardClass = "border-amber-500/40 bg-amber-500/5 hover:border-amber-500/70";
    roleLabel = "Child";
    roleColorClass = "text-amber-600";
    labelBg = "bg-amber-500/10";
  }

  return (
    <div className="flex flex-col items-center">
      {/* Node Card */}
      <div
        className={`flex relative flex-col items-center justify-center p-4 min-w-[210px] max-w-[260px] border-2 rounded-2xl text-center shadow-xs transition-all duration-300 ${cardClass}`}
      >
        <span className={`text-[11px] font-extrabold uppercase tracking-wider px-2.5 py-0.5 rounded-full ${roleColorClass} ${labelBg} mb-2`}>
          {node.species || "Cow"}
        </span>
        <span className="font-serif text-lg font-black text-foreground truncate w-full">
          {node.animal_name}
        </span>
        {node.breed && (
          <span className="text-xs font-bold text-muted-foreground mt-1">
            {node.breed}
          </span>
        )}

        {(node.mother_name || node.father_name) && (
          <div className="text-[10px] mt-3 pt-2.5 border-t border-border/40 w-full flex flex-col gap-1 items-stretch">
            {node.mother_name && (
              <span className="flex items-center justify-between bg-pink-500/10 text-pink-700 dark:text-pink-300 px-2.5 py-1 rounded-md font-medium">
                <span className="font-extrabold text-[9px] uppercase tracking-wide">Mother</span>
                <span className="truncate max-w-[110px] text-xs font-black">{node.mother_name}</span>
              </span>
            )}
            {node.father_name && (
              <span className="flex items-center justify-between bg-sky-500/10 text-sky-700 dark:text-sky-300 px-2.5 py-1 rounded-md font-medium">
                <span className="font-extrabold text-[9px] uppercase tracking-wide">Father</span>
                <span className="truncate max-w-[110px] text-xs font-black">{node.father_name}</span>
              </span>
            )}
          </div>
        )}

        {roleLabel && (
          <span className={`mt-2.5 absolute -bottom-2 text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full tracking-wider text-white ${isSelected ? "bg-[#0052b4]" : isParent ? "bg-emerald-600" : "bg-amber-600"
            }`}>
            {roleLabel}
          </span>
        )}
      </div>


      {/* Children Render */}
      {hasChildren && (
        <div className="flex flex-col items-center w-full mt-2">
          {/* Vertical line down from parent */}
          <div className="w-0.5 h-6 bg-[#0052b4]/30"></div>

          {/* Children flex wrapper */}
          <div className="flex justify-center">
            {node.children!.map((child, index) => {
              const isFirst = index === 0;
              const isLast = index === node.children!.length - 1;

              return (
                <div key={child.id} className="relative flex flex-col items-center px-4 pt-4">
                  {/* Horizontal line for left side */}
                  {!isFirst && (
                    <div className="absolute top-0 left-0 right-1/2 border-t-2 border-[#0052b4]/30"></div>
                  )}
                  {/* Horizontal line for right side */}
                  {!isLast && (
                    <div className="absolute top-0 left-1/2 right-0 border-t-2 border-[#0052b4]/30"></div>
                  )}
                  {/* Vertical line down to child */}
                  <div className="absolute top-0 h-4 w-0.5 bg-[#0052b4]/30"></div>

                  <TreeNodeComponent node={child} selectedId={selectedId} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Lineage Visual Tree Component
function FamilyTreeView({ animalOptions }: { animalOptions: LookupOption[] }) {
  const [selectedAnimalId, setSelectedAnimalId] = useState<string>("");
  const [treeData, setTreeData] = useState<FamilyTreeNode | null>(null);
  const [loadingTree, setLoadingTree] = useState(false);

  useEffect(() => {
    if (!selectedAnimalId) {
      setTreeData(null);
      return;
    }
    const loadTree = async () => {
      setLoadingTree(true);
      try {
        const data = await animalService.getFamilyTree(Number(selectedAnimalId));
        if (data) {
          setTreeData(data);
        } else {
          setTreeData(null);
        }
      } catch (err) {
        console.error(err);
        projectToast.error("Failed to load family tree data");
        setTreeData(null);
      } finally {
        setLoadingTree(false);
      }
    };
    loadTree();
  }, [selectedAnimalId]);

  return (
    <div className="space-y-8 bg-card border border-border/60 rounded-3xl p-6 shadow-xs">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center justify-between">
        <div>
          <h3 className="font-serif text-lg font-extrabold text-foreground">Animal Lineage Tree</h3>
          <p className="text-xs text-muted-foreground">Select an animal to visualize its maternal and paternal family tree</p>
        </div>
        <div className="w-64">
          <SearchableSelect
            options={animalOptions}
            value={selectedAnimalId}
            onValueChange={setSelectedAnimalId}
            placeholder="Select an animal..."
          />
        </div>
      </div>

      {loadingTree ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#0052b4] border-t-transparent" />
        </div>
      ) : treeData ? (
        <div className="overflow-x-auto py-6">
          <div className="min-w-[800px] flex justify-center p-4">
            <TreeNodeComponent node={treeData} selectedId={Number(selectedAnimalId)} />
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="rounded-full bg-muted/60 p-4 mb-4">
            <Sprout className="h-8 w-8 text-muted-foreground" />
          </div>
          <h4 className="font-serif text-base font-bold text-foreground">No Animal Selected</h4>
          <p className="text-xs text-muted-foreground mt-1 max-w-sm">Use the dropdown menu above to select an animal and view its complete lineage tree.</p>
        </div>
      )}
    </div>
  );
}
